import { useCallback, useEffect, useState } from 'react';
import { fetchWithAuth } from './utils/api';

const serverUrl =
  process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

// Parses JSON and throws on non-2xx. Returns null if fetchWithAuth already
// handled a 401 (session expired / redirecting).
async function apiCall(path, options = {}) {
  const response = await fetchWithAuth(`${serverUrl}${path}`, options);

  if (!response) return null; // 401 already handled by fetchWithAuth (redirecting)

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || `Request failed: ${response.status}`);
  }

  return data;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);

  return Uint8Array.from(
    [...rawData].map((char) => char.charCodeAt(0))
  );
}

// `isAuthenticated` gates every network call this hook makes. Default is
// `true` so existing call sites that don't pass it keep working exactly as
// before; pass `false` explicitly (e.g. from a route that can render while
// logged out) to make the hook a pure no-op against the backend.
export function usePushNotifications(isAuthenticated = true) {
  const [supported, setSupported] = useState(false);

  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined'
      ? Notification.permission
      : 'default'
  );

  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'subscribed' | 'unsubscribed' | null

  // Initial status/reconciliation loading.
  // This is used while the hook is registering the service worker and
  // checking whether the existing browser subscription also exists
  // on the backend.
  //
  // IMPORTANT:
  // This is NOT used for the Enable/Disable button API calls.
  const [loading, setLoading] = useState(true);

  // Loading state specifically for a user-triggered subscribe/unsubscribe
  // operation.
  //
  // This prevents multiple clicks while the current operation is still
  // communicating with the browser and/or backend.
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState(null);

  // Register the SW, then reconcile whatever the browser has locally
  // against the backend. Local pushManager state is never trusted on its
  // own — it only tells us whether a subscription exists client-side; the
  // backend record (or lack of it) is what actually drives `subscribed`.
  useEffect(() => {
    const isSupported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setSupported(isSupported);

    // Bail before any backend call if unsupported OR not logged in.
    // This is the fix for the 401 redirect loop on /login: without a
    // session cookie, subscription-status will always 401, which
    // previously fired on every mount (e.g. every remount caused by
    // fetchWithAuth's own window.location.href redirect).
    if (!isSupported || !isAuthenticated) {
      setLoading(false);
      return;
    }

    // Guards against this effect's result being overwritten by a stale,
    // still-in-flight run — e.g. isAuthenticated flipping more than once
    // during app bootstrap, or the effect otherwise firing again before
    // the previous async chain has resolved. Not StrictMode-specific:
    // any double invocation in production hits this same race without
    // it, since whichever setState call lands last wins regardless of
    // which run it actually belongs to.
    let cancelled = false;

    (async () => {
      try {
        const registration =
          await navigator.serviceWorker.register('/sw.js');

        await navigator.serviceWorker.ready;

        const existing =
          await registration.pushManager.getSubscription();

        if (cancelled) return;

        if (!existing) {
          setSubscribed(false);
          return;
        }

        // "Enabled" only if the backend also has this endpoint.
        // Read-only check — no auto re-subscribe on mismatch. Browser
        // subscription can outlive an explicit Disable (backend row
        // gone, browser object still there); writing it back here would
        // silently undo that. Stale local subscription just sits unused
        // until the user clicks Enable again.
        try {
          const result = await apiCall(
            `/api/push-notification/subscription-status?endpoint=${encodeURIComponent(
              existing.endpoint
            )}`
          );

          if (cancelled) return;
          if (result === null) return; // session expired mid-check

          setSubscriptionStatus(result.status);
          setSubscribed(result.status === 'subscribed');
        } catch (syncErr) {
          if (cancelled) return;

          // Backend check failed — don't claim "Enabled" for something
          // we can't actually confirm the backend has.
          setSubscribed(false);
          setSubscriptionStatus(null);
          setError(syncErr.message);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Keep `permission` live if the user changes it via the browser's site
  // settings UI while the tab is open, instead of only reading it once.
  // Pure browser API — no auth/network involved, so this runs regardless
  // of login state.
  useEffect(() => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.permissions
    ) {
      return;
    }

    let status;
    let cancelled = false;

    navigator.permissions
      .query({ name: 'notifications' })
      .then((permStatus) => {
        if (cancelled) return;

        status = permStatus;

        status.onchange = () => {
          setPermission(Notification.permission);
        };
      })
      .catch(() => {
        // permissions API for 'notifications' unsupported in this browser;
        // permission state just won't live-update, which is fine.
      });

    return () => {
      cancelled = true;

      if (status) {
        status.onchange = null;
      }
    };
  }, []);

  // IMPORTANT: this must be called directly from a user-gesture handler
  // (e.g. a button's onClick), not from a useEffect or auto-run logic.
  // Chrome only shows the native permission popup while "user activation"
  // is active; if it's called automatically on mount/login with no click
  // behind it, the browser can silently skip the prompt and just resolve
  // with whatever Notification.permission already is.
  const subscribe = useCallback(async () => {
    // Prevent overlapping subscribe operations.
    // This is also a safety guard in case multiple callers invoke
    // subscribe() before the UI has had a chance to disable its button.
    if (actionLoading) return;

    // Self-defense: don't let this hit the backend if called while
    // logged out, regardless of which caller invokes it (auto-subscribe,
    // a manual "Enable notifications" button, etc.).
    if (!isAuthenticated) {
      setError('You must be logged in to enable notifications.');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      if (Notification.permission === 'denied') {
        // Browsers never re-show the popup once denied — the user has to
        // flip it in site settings themselves. Requesting again here would
        // silently no-op, so surface the real instruction instead.
        throw new Error(
          "Notifications are blocked. Enable them in your browser's site settings, then click Enable again."
        );
      }

      // Call requestPermission() FIRST, before any other await. User
      // activation from the click can be consumed by earlier awaits
      // (e.g. waiting on the service worker), which causes Chrome to
      // suppress the popup on the very case you hit: permission stuck on
      // "default" with no dialog ever shown, no error thrown either.
      const permissionResult =
        await Notification.requestPermission();

      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error(
          'Notification permission was not granted'
        );
      }

      const registration =
        await navigator.serviceWorker.ready;

      const keyData = await apiCall(
        '/api/push-notification/vapid-public-key'
      );

      if (!keyData) return; // session expired mid-flow

      // pushManager.subscribe() is idempotent: if a subscription already
      // exists with the same applicationServerKey, it's returned as-is
      // with no new permission prompt.
      const subscription =
        await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey:
            urlBase64ToUint8Array(keyData.publicKey),
        });

      const result = await apiCall(
        '/api/push-notification/subscribe',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subscription),
        }
      );

      if (!result) return; // session expired mid-flow

      setSubscriptionStatus('subscribed');
      setSubscribed(true);
    } catch (err) {
      setError(err.message);

      // Don't leave `subscribed` stale/true if any step above failed —
      // the browser may hold a live subscription the backend never got.
      setSubscribed(false);
      setSubscriptionStatus(null); // unknown — don't assert 'unsubscribed' when we're not sure what state the backend is actually in

    } finally {
      // Always release the action lock, regardless of whether the
      // operation succeeded, failed, or returned early.
      setActionLoading(false);
    }
  }, [actionLoading, isAuthenticated]);

  const unsubscribe = useCallback(async () => {
    // Prevent overlapping unsubscribe operations.
    // This protects against rapid repeated clicks as well as accidental
    // duplicate calls from another component.
    if (actionLoading) return;

    setActionLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription && isAuthenticated) {
        try {
          await apiCall('/api/push-notification/unsubscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint: subscription.endpoint }),
          });
        } catch (err) {
          setError(err.message || 'Failed to unsubscribe on server');
        }
      }

      // Deliberately NOT calling subscription.unsubscribe() here. Removing
      // the browser-level object destroys its endpoint — and without an
      // endpoint, the reconciliation effect has nothing to query on the
      // next reload, so it can never learn this device was explicitly
      // opted out (backend status stays unreachable, defaults to null,
      // and auto-subscribe fires again). Leaving it alive is harmless:
      // nothing pushes to an endpoint the backend has marked unsubscribed.
      setSubscribed(false);
      setSubscriptionStatus('unsubscribed');
    } catch (err) {
      setError(err.message);
      setSubscribed(false);
    } finally {
      // Always release the action lock after the operation completes.
      setActionLoading(false);
    }
  }, [actionLoading, isAuthenticated]);

  const sendTestNotification = useCallback(async (payload) => {
    if (!isAuthenticated) return;

    setError(null);

    try {
      await apiCall('/api/push-notification/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      setError(err.message);
    }
  }, [isAuthenticated]);

  return {
    supported,
    permission,
    subscribed,
    subscriptionStatus,

    // Initial service-worker/backend reconciliation state.
    loading,

    // User-triggered Enable/Disable operation state.
    actionLoading,

    error,
    subscribe,
    unsubscribe,
    sendTestNotification,
  };
}